import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { Box, Button, Divider, useTheme } from '@mui/material'
import React from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { useUserSettings } from 'src/Hooks'
import useModals from 'src/Hooks/useModals'
import { Ai360PowerShareLogo } from '../../../resources'
import { ExtensionCategoryNames, ExtensionDetailsProps, ExtensionState, ExtensionTypes } from '../../../types'
import { DetailsViewWrapper, DisconnectButton, HeaderContent } from '../../general'
import { useOpenInexogyModal } from '../../../hooks/inexogy'
import useInstallationExtensionData from '../../../hooks/extensions/use-installation-extension-data'
import { useCopyToClipboard } from 'src/Elements/CopyToClipboard/hook'

const TARIFF_URL = 'https://www.e3dc.com/power/'

/**
 * Gives a detailed description of the ai360 power share extension.
 */
const Ai360PowerShareDetails: React.FC<ExtensionDetailsProps> = ({ extensionState, hasActiveTariff, isLoading, onClose, place = 0 }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const openInexogyModal = useOpenInexogyModal()
  const isConnected = extensionState === 'active'
  const contractStateProps = {
    contractState: extensionState,
    isLoading,
  }

  return (
    <DetailsViewWrapper
      extensionType={ExtensionTypes.Ai360PowerShare}
      descriptions={isConnected ? [] : [t('installation-extensions.ai360-power-share.details.description')]}
      headerContent={<HeaderContent logo={<Ai360PowerShareLogo />} />}
      bodyContent={isConnected ? <ConnectedAi360PowerShareContent /> : undefined}
      footerContent={<FooterContent hasActiveTariff={hasActiveTariff} extensionState={extensionState} />}
      onClose={onClose}
      place={place}
      open={true}
      category={{ name: ExtensionCategoryNames.ProsumerTariff, paletteColor: theme.palette.primary, variant: 'light' }}
      contractStateProps={contractStateProps}
      linkHref="#"
      onLinkClick={openInexogyModal}
    />
  )
}

const ConnectedAi360PowerShareContent: React.FC = () => {
  const { t } = useCustomTranslation()
  const { copyToClipboard } = useCopyToClipboard()

  const handlePortalClick = (): void => {
    window.open(TARIFF_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box>
      <Box>
        <Typography variant="overline" fontWeight="700">
          {t('installation-extensions.ai360-power-share.details.share-electricity')}
        </Typography>
        <Typography variant="body2" marginBlock={2}>
          {t('installation-extensions.ai360-power-share.details.share-electricity-description')}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Button variant="outlined" onClick={handlePortalClick}>
            {t('installation-extensions.ai360-power-share.details.power-share-portal')}
          </Button>
        </Box>
      </Box>

      <Box mt={4}>
        <Typography variant="overline" fontWeight="700">
          {t('common.invite')}
        </Typography>
        <Typography variant="body2" marginBlock={2}>
          {t('installation-extensions.ai360-power-share.details.invite-description')}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Button variant="outlined" onClick={() => copyToClipboard(TARIFF_URL)}>
            {t('installation-extensions.ai360-power-share.details.copy-link')}
          </Button>
        </Box>
      </Box>

      <Divider sx={theme => ({ borderColor: theme.palette.shades.shade03, marginBlock: 4 })} />
    </Box>
  )
}

interface FooterContentProps {
  hasActiveTariff?: boolean
  extensionState?: ExtensionState
}

const FooterContent: React.FC<FooterContentProps> = ({ hasActiveTariff, extensionState }) => {
  const { t } = useCustomTranslation()
  const { userSettings } = useUserSettings()
  const { pageActions } = useNewRelic()
  const { openAi360SmartMeterRecommendationModal, openAi360PowerShareDeleteConnectionModal } = useModals()
  const installationId = useTypedSelector(state => state.storage.activeInstallation)?.toString() ?? ''
  const { loadInstallationExtensionStates } = useInstallationExtensionData(installationId)
  const isConnected = extensionState === 'active'

  const onClick = (): void => {
    pageActions.componentClicked({
      componentName: 'Ai360PowerShareDetailsDialog',
      userId: userSettings.userid?.toString(),
    })
    openAi360SmartMeterRecommendationModal({
      tariffType: ExtensionTypes.Ai360PowerShare,
      onContinue: () => {
        window.open(TARIFF_URL, '_blank', 'noopener,noreferrer')
      },
    })
  }

  const handleDisconnectExtension = (): void => {
    openAi360PowerShareDeleteConnectionModal({ loadInstallationExtensionStates })
  }

  if (isConnected) return <DisconnectButton isConnected={isConnected} onClickLink={handleDisconnectExtension} />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <OptionalTooltipWrapper hasTooltip={hasActiveTariff} titleKey={'installation-extensions.ai360-power-share.connection-button.disabled-tooltip'}>
        <Button variant="outlined" onClick={onClick} disabled={hasActiveTariff}>
          {t('installation-extensions.ai360-power-share.modal.button')}
        </Button>
      </OptionalTooltipWrapper>
      <Typography variant="overline" fontWeight="700">
        {t('installation-extensions.coming-soon')}
      </Typography>
    </Box>
  )
}

export default Ai360PowerShareDetails
